import {
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core"
import { IconCheck } from "@tabler/icons-react"
import { Button, Pagination } from "@tfg/ui"
import { Fragment, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { useFindValidInformationRequests } from "~/gen/api/hooks"
import { FindValidInformationRequestsForSupplierResponseDto } from "~/gen/api/types"
import { CheckList } from "~/pages/SupplierPortal/TraceabilityRequests/TraceabilityRequestsList.tsx"
import { useSubmitRequestsMutation } from "~/pages/SupplierPortal/TraceabilityRequests/hooks.ts"
import { getComponentComposition } from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"
import { useGetTranslationCallback } from "~/utils/translations"

interface Props {
  opened: boolean
  onClose: () => void
  informationRequestIds: number[]
  onRequestsSubmitted: () => void
}
const REQUESTS_PER_PAGE = 10

export const SubmitRequestsModal = ({
  opened,
  onClose,
  informationRequestIds,
  onRequestsSubmitted,
}: Props) => {
  const { t } = useTranslation(["traceability"])

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: REQUESTS_PER_PAGE,
  })

  const [certify, setCertify] = useState<boolean>(false)
  const closeModal = () => {
    onClose()
    setCertify(false)
    setCheckList({})
  }

  const [checkList, setCheckList] = useState<CheckList>({})

  const { mutate: submitRequests, isPending: isLoading } =
    useSubmitRequestsMutation({
      onSuccess: (_data, variables) => {
        setCheckList({})
        showSuccessNotification({
          title: t("submitModal.confirmation", {
            count: variables.requests.length,
          }),
          message: t("submitModal.confirmation", {
            count: variables.requests.length,
          }),
        })
        queryClient.invalidateQueries({
          queryKey: ["listTraceabilityRequests"],
        })
        onClose()
      },
      onError: (error: unknown) => {
        showAxiosError({
          error: error as Error,
          notificationTitle: "Error",
          notificationMessage: t("submitModal.error"),
          showNotification: true,
        })
        setCertify(false)
      },
      onSettled: onRequestsSubmitted,
    })

  const handleSubmitRequests = (
    requests: FindValidInformationRequestsForSupplierResponseDto[]
  ) => {
    submitRequests({
      requests: requests.map((request) => ({
        id: request.id,
        request: {
          operations: request.operationItems.map((operationItem) => {
            return {
              operationId: operationItem.operation?.id ?? -1, // Very lazy way of not having to do crazy typing
              reference:
                operationItem.operation?.reference ??
                operationItem.item.reference,
            }
          }),
        },
      })),
    })
  }

  const validRequestsQuery = useFindValidInformationRequests({
    query: {
      enabled: opened,
    },
  })

  const validRequests = useMemo(
    () => validRequestsQuery.data?.data ?? [],
    [validRequestsQuery.data?.data]
  )

  const allChecked: CheckList = useMemo(() => {
    const checked: CheckList = {}
    validRequests?.forEach((request) => (checked[request.id] = true))
    return checked
  }, [validRequests])

  useEffect(() => {
    if (informationRequestIds) {
      setCheckList((prev) =>
        informationRequestIds.reduce<CheckList>(
          (acc, informationRequestId) => ({
            ...acc,
            [informationRequestId]: true,
          }),
          prev
        )
      )
    }
  }, [informationRequestIds, setCheckList])

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={
        <Text size="lg">
          <strong>{t("submitModal.title")}</strong>
        </Text>
      }
      returnFocus={false}
      size="100vw"
    >
      <Stack>
        <Group justify="left">
          <Button variant="secondary" onClick={() => setCheckList(allChecked)}>
            {t("selectAll")}
          </Button>
          <Button variant="secondary" onClick={() => setCheckList({})}>
            {t("deselectAll")}
          </Button>
          <Pagination
            value={pagination.page + 1}
            onChange={(page) =>
              setPagination((prev) => ({ ...prev, page: page - 1 }))
            }
            total={Math.ceil(validRequests.length / REQUESTS_PER_PAGE)}
          />
        </Group>

        <ScrollArea
          style={{ width: "100%" }}
          h="calc(100vh - 400px)"
          type="always"
          offsetScrollbars
        >
          <LoadingOverlay
            visible={isLoading || validRequestsQuery.isLoading}
            overlayProps={{ blur: 2 }}
          />
          {validRequests.length ? (
            <Table verticalSpacing="xs" highlightOnHover={true}>
              <thead>
                <tr>
                  <th />
                  <th>
                    <Title order={4}>{t("submitModal.type")}</Title>
                  </th>
                  <th>
                    <Title order={4}>{t("submitModal.reference")}</Title>
                  </th>
                  <th>
                    <Title order={4}>{t("submitModal.composition")}</Title>
                  </th>
                </tr>
              </thead>
              <tbody>
                {validRequests
                  .slice(
                    pagination.page * pagination.pageSize,
                    (pagination.page + 1) * pagination.pageSize
                  )
                  .map((request) => (
                    <Row
                      key={request.id}
                      request={request}
                      checked={!!checkList[request.id]}
                      update={(id: number) =>
                        setCheckList({
                          ...checkList,
                          [id]: !checkList[id],
                        })
                      }
                    />
                  ))}
              </tbody>
            </Table>
          ) : (
            <Group justify="center">
              <Text c="gray">{t("submitModal.noRequests")}</Text>
            </Group>
          )}
        </ScrollArea>
        <Group justify="right">
          <Checkbox
            label={t("submitModal.confidenceStatement")}
            onChange={(event) => {
              setCertify(event.currentTarget.checked)
            }}
          />
          <Button
            icon={<IconCheck />}
            onClick={() => {
              handleSubmitRequests(
                validRequests.filter((request) => {
                  return checkList[request.id]
                })
              )
            }}
            disabled={
              !certify || !Object.values(checkList).filter((x) => x).length
            }
          >
            {t("submitModal.submitButton", {
              itemCount: Object.values(checkList).filter((x) => x).length,
            })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

type RowProps = {
  request: FindValidInformationRequestsForSupplierResponseDto
  checked: boolean | undefined
  update: (val: number) => void
  color?: string
}

function Row({ request, checked, update, color = "green" }: RowProps) {
  return (
    <tr onClick={() => update(request.id)}>
      <td>
        <Checkbox
          onChange={() => update(request.id)}
          checked={checked}
          color={color}
        />
      </td>
      <td>
        <b>
          <Text style={{ textTransform: "capitalize" }}>
            {request.item.type}
          </Text>
        </b>
      </td>
      <td>{request.item.reference}</td>
      <td>
        <Text>
          <ItemComposition item={request.item} />
        </Text>
      </td>
    </tr>
  )
}

type ItemCompositionProps = {
  item: FindValidInformationRequestsForSupplierResponseDto["item"]
}

export function ItemComposition({ item }: ItemCompositionProps) {
  const getTranslation = useGetTranslationCallback()

  switch (item.type) {
    case "product": {
      return (
        <>
          {item.children.map((c, index) => (
            <Fragment key={index}>
              <b>{c.reference}</b>: {getComponentComposition(c, getTranslation)}
              <br />
            </Fragment>
          ))}
        </>
      )
    }
    case "component": {
      return <>{getComponentComposition(item, getTranslation)}</>
    }
    case "material": {
      return <>{item.reference} </>
    }
  }
}
